import { router } from 'expo-router';
import { ReactNode } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  TouchableOpacityProps,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

import { useCart } from '@fhs/cart';
import { useMenuClickEvents } from '@fhs/menu/src/analytics';
import { IconShoppingBag, Text, tokens } from '@fhs/ui';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';

export type HeaderCartButtonProps = Omit<TouchableOpacityProps, 'children'>;
const ShoppingBagContainer = ({
  children,
  isDisabled,
  ctaDisabledEvent,
  headerCartProps,
  isCartServiceEnabled,
}: {
  children: ReactNode;
  isDisabled: boolean;
  ctaDisabledEvent: () => void;
  isCartServiceEnabled: boolean;
  headerCartProps?: HeaderCartButtonProps;
}) => {
  if (isDisabled) {
    return (
      <TouchableWithoutFeedback
        onPress={() => {
          ctaDisabledEvent();
        }}
        accessibilityRole="button"
        aria-disabled={true}
      >
        <View style={styles.container}>{children}</View>
      </TouchableWithoutFeedback>
    );
  }
  return (
    <TouchableOpacity
      style={styles.container}
      {...headerCartProps}
      onPress={e => {
        isCartServiceEnabled ? router.setParams({ drawer: 'cart' }) : headerCartProps.onPress?.(e);
      }}
    >
      {children}
    </TouchableOpacity>
  );
};
export function HeaderCartButton(props: HeaderCartButtonProps) {
  const enableCartService = useFlag<boolean>(LaunchDarklyFlag.ENABLE_SIMPLY_BETTER_CART_SERVICE);
  const { cart, isPending } = useCart();
  const { numCartPreviewEntries, addItemToCartLoading } = useOrderContext();
  // TODO: Add a loading state to cart entries length for on initial mount
  const entries = enableCartService ? cart?.entries.length : numCartPreviewEntries;
  const isCartEmpty = !enableCartService && entries < 1;
  const { logEmptyCartCTAClick } = useMenuClickEvents();
  return (
    <ShoppingBagContainer
      isDisabled={isCartEmpty}
      ctaDisabledEvent={logEmptyCartCTAClick}
      headerCartProps={props}
      isCartServiceEnabled={enableCartService}
    >
      <View>
        <IconShoppingBag size={32} />
      </View>
      {!isPending && (
        <View
          style={[
            styles.badgeRounded,
            {
              backgroundColor: isCartEmpty
                ? tokens.colors.$houseLight
                : tokens.colors.$houseRedDarken,
            },
          ]}
        >
          <Text.Paragraph
            style={{
              color: isCartEmpty ? tokens.colors.$black : tokens.colors.$white,
              justifyContent: 'center',
              alignContent: 'center',
            }}
            size="xs"
            weight="bold"
          >
            {addItemToCartLoading ? (
              <ActivityIndicator
                color={isCartEmpty ? tokens.colors.$houseRedDarken : 'white'}
                size="small"
                style={{ transform: [{ scale: 0.5 }], paddingTop: 1 }}
              />
            ) : (
              entries
            )}
          </Text.Paragraph>
        </View>
      )}
    </ShoppingBagContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-end',
    position: 'relative',
  },
  badgeRounded: {
    width: 20,
    height: 20,
    borderRadius: 100,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: -10,
    right: -10,
  },
});
